import React, { useEffect, useState } from 'react';
import { Card, Form, Input, Button, Row, Col, Space, Breadcrumb, Divider, Typography, Alert } from 'antd';
import { showSuccess, showError } from '../../../../components/errorHandler';
import { apiRequest } from '../../../../service/apiRequest';
import { WhatsAppOutlined, SendOutlined, SafetyCertificateOutlined } from '@ant-design/icons';

const { Text, Paragraph } = Typography;

const WhatsappSettings: React.FC = () => {
  const [form] = Form.useForm();
  const [testForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const response = await apiRequest({ method: 'get', url: '/whatsapp-setting' });
        console.log('Fetched Whatsapp settings:', response);
        if (response) {
          form.setFieldsValue({
            ws_whatsapp_sender_id: response.ws_whatsapp_sender_id || response.ws_phone_number_id || '',
            ws_whatsapp_api_key: response.ws_whatsapp_api_key || response.ws_access_token || '',
            ws_waba_id: response.ws_waba_id || '',
            ws_otp_template_name: response.ws_otp_template_name || '',
          });
        }
      } catch (error) {
        showError('Failed to load Whatsapp settings');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [form]);

  const handleSave = async (values: any) => {
    try {
      setLoading(true);
      await apiRequest({
        method: 'put',
        url: '/save-whatsapp-settings',
        data: values,
      });
      showSuccess('WhatsApp Cloud API settings saved successfully');
    } catch (error) {
      showError('Failed to save WhatsApp settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSendTestMessage = async (values: any) => {
    try {
      setTestLoading(true);
      const res = await apiRequest<{ message?: string; status?: number }>({
        method: 'post',
        url: '/send-test-whatsapp',
        data: {
          mobile: values.test_mobile,
          message: 'Hello from SVICON! Your WhatsApp Cloud API integration is working successfully.',
        },
      });
      showSuccess(res?.message || 'Test message sent successfully to WhatsApp!');
    } catch (error: any) {
      showError(error?.response?.data?.message || 'Failed to send test WhatsApp message. Please check your credentials.');
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Breadcrumb items={[{ title: 'Dashboard' }, { title: 'WhatsApp Cloud API Settings' }]} />

      <Row gutter={24} justify="center">
        <Col xs={24} lg={14}>
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <WhatsAppOutlined style={{ color: '#25D366', fontSize: 22 }} />
                <span>Meta WhatsApp Cloud API Configuration</span>
              </div>
            }
            bordered
          >
            <Alert
              message="WhatsApp Cloud API for Login OTP"
              description="Configure your Meta WhatsApp Cloud API credentials. When users (other than super-master) log in, a secure verification OTP will be automatically delivered to their registered WhatsApp number."
              type="info"
              showIcon
              icon={<SafetyCertificateOutlined style={{ color: '#1890ff' }} />}
              style={{ marginBottom: 20 }}
            />

            <Form
              layout="vertical"
              form={form}
              onFinish={handleSave}
              initialValues={{
                ws_whatsapp_sender_id: '',
                ws_whatsapp_api_key: '',
                ws_waba_id: '',
                ws_otp_template_name: '',
              }}
            >
              <Form.Item
                label="Phone Number ID (Sender ID)"
                name="ws_whatsapp_sender_id"
                tooltip="Your WhatsApp Cloud API Phone Number ID from Meta Developer Portal"
                rules={[{ required: true, message: 'Phone Number ID is required' }]}
              >
                <Input placeholder="e.g. 104829104819201" />
              </Form.Item>

              <Form.Item
                label="System User Access Token / API Key"
                name="ws_whatsapp_api_key"
                tooltip="Permanent access token generated from Meta Business Manager with whatsapp_business_messaging permissions"
                rules={[{ required: true, message: 'Access Token is required' }]}
              >
                <Input.Password placeholder="EAABw..." />
              </Form.Item>

              <Form.Item
                label="WhatsApp Business Account ID (WABA ID) [Optional]"
                name="ws_waba_id"
                tooltip="Your WhatsApp Business Account ID from Meta Business Suite"
              >
                <Input placeholder="e.g. 109283746592019" />
              </Form.Item>

              <Form.Item
                label="OTP Template Name [Optional]"
                name="ws_otp_template_name"
                tooltip="Approved Meta WhatsApp Authentication/OTP Template name (e.g. auth_otp_code). If empty, text message is sent."
              >
                <Input placeholder="e.g. auth_otp_code (or leave empty for direct message)" />
              </Form.Item>

              <Form.Item style={{ marginTop: 24 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                  size="large"
                  style={{ backgroundColor: '#25D366', borderColor: '#25D366', fontWeight: 700 }}
                  icon={<WhatsAppOutlined />}
                >
                  Save WhatsApp Settings
                </Button>
              </Form.Item>
            </Form>

            <Divider style={{ margin: '24px 0' }} />

            <div style={{ marginTop: 12 }}>
              <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 12 }}>
                Test WhatsApp API Connection
              </Text>
              <Form form={testForm} layout="inline" onFinish={handleSendTestMessage}>
                <Form.Item
                  name="test_mobile"
                  style={{ flex: 1 }}
                  rules={[
                    { required: true, message: 'Enter recipient mobile' },
                    { pattern: /^[0-9]{10,12}$/, message: 'Enter valid 10-12 digit mobile with country code' },
                  ]}
                >
                  <Input placeholder="Recipient Mobile (e.g. 919876543210)" />
                </Form.Item>
                <Form.Item>
                  <Button
                    type="default"
                    htmlType="submit"
                    loading={testLoading}
                    icon={<SendOutlined />}
                  >
                    Send Test WhatsApp
                  </Button>
                </Form.Item>
              </Form>
            </div>
          </Card>
        </Col>
      </Row>
    </Space>
  );
};

export default WhatsappSettings;
